/**
 * Peach Payments reducer with actions
 * @module reducers/peachPayment
 */

// Peach Payments action constants
export const PEACH_CHECKOUT_CREATE = 'PEACH_CHECKOUT_CREATE';
export const PEACH_CHECKOUT_STATUS = 'PEACH_CHECKOUT_STATUS';
export const PEACH_CHECKOUT_PROCESS = 'PEACH_CHECKOUT_PROCESS';
export const PEACH_CHECKOUT_RESET = 'PEACH_CHECKOUT_RESET';

// Action creators
export function createPeachCheckout(
  amount: number,
  currency: string,
  subscriptionType: string
) {
  // console.log('createPeachCheckout: ', amount)
  return {
    type: PEACH_CHECKOUT_CREATE,
    request: {
      op: 'post',
      path: '/@peach-checkout',
      data: {
        amount,
        currency,
        subscription_type: subscriptionType,
      },
    },
  };
}

export function getPeachCheckoutStatus(checkoutId: string) {
  return {
    type: PEACH_CHECKOUT_STATUS,
    request: {
      op: 'get',
      path: `/@peach-checkout/status?checkoutId=${checkoutId}`,
    },
  };
}

export function processPeachPayment(
  checkoutId: string,
  subscriptionType: string,
  paymentData: any
) {
  return {
    type: PEACH_CHECKOUT_PROCESS,
    request: {
      op: 'post',
      path: '/@peach-checkout/process',
      data: {
        checkoutId,
        subscription_type: subscriptionType,
        payment: paymentData,
      },
      headers: {
        'X-Csrf-Token': 'fetch-from-cookie',  // This tells Volto to get the token
      },
    },
  };
}

export function resetPeachCheckout() {
  return {
    type: PEACH_CHECKOUT_RESET,
  };
}

// State interfaces
interface PeachCheckoutState {
  error: any;
  loaded: boolean;
  loading: boolean;
  checkoutId: string | null;
  entityId: string | null;
  timestamp: string | null;
}

interface PeachStatusState {
  error: any;
  loaded: boolean;
  loading: boolean;
  status: string | null;
  description: string | null;
  payment: any;
}

interface PeachProcessState {
  error: any;
  loaded: boolean;
  loading: boolean;
  success: boolean;
  message: string | null;
}

interface PeachPaymentState {
  checkout: PeachCheckoutState;
  status: PeachStatusState;
  process: PeachProcessState;
}

// Initial states
const initialCheckoutState: PeachCheckoutState = {
  error: null,
  loaded: false,
  loading: false,
  checkoutId: null,
  entityId: null,
  timestamp: null,
};

const initialStatusState: PeachStatusState = {
  error: null,
  loaded: false,
  loading: false,
  status: null,
  description: null,
  payment: null,
};

const initialProcessState: PeachProcessState = {
  error: null,
  loaded: false,
  loading: false,
  success: false,
  message: null,
};

const initialState: PeachPaymentState = {
  checkout: initialCheckoutState,
  status: initialStatusState,
  process: initialProcessState,
};

// Reducer
export default function peachPayment(
  state: PeachPaymentState = initialState,
  action: any = {}
): PeachPaymentState {
 // console.log('peachPayment Reducer: state: ', state, ' action: ', action);
  switch (action.type) {
    // Checkout creation
    case `${PEACH_CHECKOUT_CREATE}_PENDING`:
      return {
        ...state,
        checkout: {
          ...state.checkout,
          error: null,
          loaded: false,
          loading: true,
        },
      };
    case `${PEACH_CHECKOUT_CREATE}_SUCCESS`:
      return {
        ...state,
        checkout: {
          ...state.checkout,
          error: null,
          loaded: true,
          loading: false,
          checkoutId: action.result.checkoutId,
          entityId: action.result.entityId,
          timestamp: action.result.timestamp,
        },
      };
    case `${PEACH_CHECKOUT_CREATE}_FAIL`:
      console.log('Error: ', action?.error?.response?.body?.error?.message);
      return {
        ...state,
        checkout: {
          ...state.checkout,
          error: action.error.response.body.error,
          loaded: false,
          loading: false,
        },
      };

    // Status check
    case `${PEACH_CHECKOUT_STATUS}_PENDING`:
      return {
        ...state,
        status: {
          ...state.status,
          error: null,
          loaded: false,
          loading: true,
        },
      };
    case `${PEACH_CHECKOUT_STATUS}_SUCCESS`:
      return {
        ...state,
        status: {
          ...state.status,
          error: null,
          loaded: true,
          loading: false,
          status: action.result.status,
          description: action.result.description,
          payment: action.result.payment,
        },
      };
    case `${PEACH_CHECKOUT_STATUS}_FAIL`:
      return {
        ...state,
        status: {
          ...state.status,
          error: action.error,
          loaded: false,
          loading: false,
        },
      };

    // Process payment (activate subscription)
    case `${PEACH_CHECKOUT_PROCESS}_PENDING`:
      return {
        ...state,
        process: {
          ...state.process,
          error: null,
          loaded: false,
          loading: true,
        },
      };
    case `${PEACH_CHECKOUT_PROCESS}_SUCCESS`:
      return {
        ...state,
        process: {
          ...state.process,
          error: null,
          loaded: true,
          loading: false,
          success: true,
          message: action.result.message,
        },
      };
    case `${PEACH_CHECKOUT_PROCESS}_FAIL`:
      return {
        ...state,
        process: {
          ...state.process,
          error: action.error,
          loaded: false,
          loading: false,
          success: false,
        },
      };

    // Reset
    case PEACH_CHECKOUT_RESET:
      return initialState;

    default:
      return state;
  }
}
